/**
 * Smart battery system — tab / thumbnail synced hero + mobile copy (Dawn-style custom element).
 */
class SmartBatterySystem extends HTMLElement {
  constructor() {
    super();
    this._onResize = this._onResize.bind(this);
    this._onTabRowScroll = this._onTabRowScroll.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    /** @type {Element[]} */
    this.panels = [];
    /** @type {Element[]} */
    this.moCopies = [];
    /** @type {Element[]} */
    this.triggers = [];
  }

  connectedCallback() {
    this._unbind();

    this.panels = Array.from(
      this.querySelectorAll("[data-smart-battery-panel]")
    );
    this.moCopies = Array.from(
      this.querySelectorAll("[data-smart-battery-mo]")
    );
    this.triggers = Array.from(
      this.querySelectorAll("[data-smart-battery-trigger]")
    );
    this.indicator = this.querySelector("[data-smart-battery-indicator]");
    this.tabRow = this.querySelector(".smart-battery-system__tab-row");
    this.tabRule = this.querySelector(".smart-battery-system__tab-rule");

    this.triggers.forEach((btn) => {
      btn.addEventListener("click", this._onTriggerClick);
    });
    if (this.tabRow) {
      this.tabRow.addEventListener("scroll", this._onTabRowScroll);
    }
    window.addEventListener("resize", this._onResize);
    requestAnimationFrame(this._onResize);
  }

  disconnectedCallback() {
    this._unbind();
  }

  _unbind() {
    this.triggers.forEach((btn) => {
      btn.removeEventListener("click", this._onTriggerClick);
    });
    if (this.tabRow) {
      this.tabRow.removeEventListener("scroll", this._onTabRowScroll);
    }
    window.removeEventListener("resize", this._onResize);
  }

  /**
   * @param {Event} ev
   */
  _onTriggerClick(ev) {
    const btn = ev.currentTarget;
    if (!(btn instanceof Element)) return;
    const idx = parseInt(
      btn.getAttribute("data-smart-battery-trigger") || "",
      10
    );
    if (Number.isNaN(idx)) return;
    this.setActive(idx);
    if (
      btn.classList.contains("smart-battery-system__tab") &&
      this.tabRow &&
      window.matchMedia("(max-width: 719px)").matches
    ) {
      btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      const finalize = () => this.moveIndicator(idx);
      this.tabRow.addEventListener("scrollend", finalize, { once: true });
      setTimeout(finalize, 360);
    }
  }

  _onTabRowScroll() {
    const sel = this.querySelector(
      '.smart-battery-system__tab[aria-selected="true"]'
    );
    if (!sel) return;
    this.moveIndicator(
      parseInt(sel.getAttribute("data-smart-battery-trigger") || "-1", 10)
    );
  }

  _onResize() {
    const sel = this.querySelector(
      '.smart-battery-system__tab[aria-selected="true"]'
    );
    if (!sel) return;
    const idx = parseInt(
      sel.getAttribute("data-smart-battery-trigger") || "-1",
      10
    );
    this.moveIndicator(idx);
  }

  /**
   * @param {number} index
   */
  setActive(index) {
    this.panels.forEach((el, i) => {
      if (i === index) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });
    this.moCopies.forEach((el, i) => {
      if (i === index) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });
    this.triggers.forEach((el) => {
      const i = parseInt(
        el.getAttribute("data-smart-battery-trigger") || "-1",
        10
      );
      const on = i === index;
      el.setAttribute("aria-selected", on ? "true" : "false");
      if (el.classList.contains("smart-battery-system__thumb")) {
        el.classList.toggle("is-active", on);
        const label = el.querySelector(".smart-battery-system__thumb-label");
        if (label) label.toggleAttribute("hidden", on);
      }
    });
    this.moveIndicator(index);
  }

  /**
   * @param {number} index
   */
  moveIndicator(index) {
    if (!this.indicator || !this.tabRow || !this.tabRule) return;
    if (window.matchMedia("(min-width: 720px)").matches) {
      this.indicator.setAttribute("hidden", "");
      return;
    }
    const tabs = this.tabRow.querySelectorAll(".smart-battery-system__tab");
    const tab = tabs[index];
    if (!tab) return;
    this.indicator.removeAttribute("hidden");
    const rl = this.tabRule.getBoundingClientRect();
    const tl = tab.getBoundingClientRect();
    this.indicator.style.width = `${tl.width}px`;
    this.indicator.style.left = `${tl.left - rl.left}px`;
  }
}

if (!customElements.get("smart-battery-system")) {
  customElements.define("smart-battery-system", SmartBatterySystem);
}
